import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail as fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { todayIso } from "@/lib/hr";

const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;

export function ProjectDialog({ project, onClose, onSaved }) {
  const [clients, setClients] = useState([]);
  const [f, setF] = useState({ title: project?.title || "", client_id: project?.client_id || "", start_date: project?.start_date || todayIso(),
    end_date: project?.end_date || "", kind: project?.kind || "pilot", target: project?.target ?? "" });
  useEffect(() => { api.get("/users", { params: { role: "client" } }).then((r) => setClients(r.data)); }, []);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    const body = { ...f, title: f.title.trim(), end_date: f.end_date || null, target: f.target === "" ? null : Number(f.target) };
    try {
      const r = project ? await api.put(`/accuracy/projects/${project.id}`, body) : await api.post("/accuracy/projects", body);
      toast.success(project ? "Project updated" : "Project created"); onSaved(r.data); onClose();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="accuracy-project-dialog">
        <DialogHeader><DialogTitle className="font-display">{project ? "Edit project" : "New project"}</DialogTitle><DialogDescription>Clients can see their own projects and accuracy notes.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Field label="Title"><Input value={f.title} onChange={(e) => set("title")(e.target.value)} maxLength={150} data-testid="project-title-input" /></Field>
          <Field label="Client">
            <Select value={f.client_id} onValueChange={set("client_id")}>
              <SelectTrigger data-testid="project-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date"><Input type="date" value={f.start_date} onChange={(e) => set("start_date")(e.target.value)} data-testid="project-start-input" /></Field>
            <Field label="End date (optional)"><Input type="date" value={f.end_date} onChange={(e) => set("end_date")(e.target.value)} data-testid="project-end-input" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <Select value={f.kind} onValueChange={set("kind")}>
                <SelectTrigger data-testid="project-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="pilot">Pilot</SelectItem><SelectItem value="commercial">Commercial</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Target % (optional)"><Input type="number" min="0" max="100" step="0.1" value={f.target} onChange={(e) => set("target")(e.target.value)} data-testid="project-target-input" /></Field>
          </div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={!f.title.trim() || !f.client_id || !f.start_date} className="bg-indigo-600 hover:bg-indigo-700" data-testid="project-save-button">{project ? "Save" : "Create project"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReadingDialog({ projectId, reading, onClose, onSaved }) {
  const [f, setF] = useState({ date: reading?.date || todayIso(), accuracy: reading?.accuracy ?? "", note: reading?.note || "" });
  const low = f.accuracy !== "" && Number(f.accuracy) < 99;
  const save = async () => {
    const body = { date: f.date, accuracy: Number(f.accuracy), note: f.note };
    try {
      if (reading) await api.put(`/accuracy/readings/${reading.id}`, body); else await api.post(`/accuracy/projects/${projectId}/readings`, body);
      toast.success("Reading saved"); onSaved(); onClose();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="accuracy-reading-dialog">
        <DialogHeader><DialogTitle className="font-display">{reading ? "Edit reading" : "Add accuracy reading"}</DialogTitle><DialogDescription>Notes are visible to the client.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date measured"><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="reading-date-input" /></Field>
            <Field label="Accuracy %"><Input type="number" min="0" max="100" step="0.1" value={f.accuracy} onChange={(e) => setF({ ...f, accuracy: e.target.value })} data-testid="reading-accuracy-input" /></Field>
          </div>
          <Field label={low ? "Reason accuracy is below 99% (required)" : "Note (optional)"}>
            <Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} maxLength={2000} className={low && !f.note.trim() ? "border-amber-400" : ""} data-testid="reading-note-input" />
          </Field>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={!f.date || f.accuracy === "" || (low && !f.note.trim())} className="bg-indigo-600 hover:bg-indigo-700" data-testid="reading-save-button">Save reading</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
